'use client';

import { useEffect, useRef } from 'react';
import {
  SectionIndexToStartingAnchor,
  SUBFLOW_STARTING_KIT_KEY,
} from '@/components/common/MultiLevelSelector/utils';

const sectionIntersectionRatios = new Map<number, number>();

let activeSectionIndex: number | null = null;

// Determines which age group section on the play kits landing page is actively being viewed
export const useTrackActivePlayKitSection = () => {
  const initialized = useRef(false);

  useEffect(() => {
    if (initialized.current) return;
    initialized.current = true;

    initIntersectioObservers();
  }, []);
};

function initIntersectioObservers() {
  const explorerSections = document.querySelectorAll(
    '[data-ga-section="explorer-section"]',
  );

  // Use a single observer for all sections to compare intersection ratios
  const observer = new IntersectionObserver(
    (entries) => {
      detectVisibility(entries);
    },
    {
      root: null,
      rootMargin: '0px',
      threshold: [0, 0.25, 0.5, 0.75, 1.0],
    },
  );

  [...explorerSections].forEach((element, sectionIndex) => {
    (element as HTMLElement).dataset.sectionIndex = `${sectionIndex}`;
    observer.observe(element);
  });
}

function detectVisibility(entries: IntersectionObserverEntry[]) {
  entries.forEach((entry) => {
    const targetElement = entry.target as HTMLElement;
    const sectionIndex = parseInt(
      targetElement.dataset.sectionIndex || '0',
      10,
    );

    // Update intersection ratio for this section
    // Only track positive ratios (intersecting sections)
    if (entry.isIntersecting) {
      sectionIntersectionRatios.set(sectionIndex, entry.intersectionRatio);
    } else {
      // Remove from map when not intersecting to only consider visible sections
      sectionIntersectionRatios.delete(sectionIndex);
    }
  });

  // Determine the most visible section (highest intersection ratio)
  updateActiveSection();
}

function updateActiveSection() {
  let maxRatio = 0;
  let newActiveSection: number | null = null;

  // Only consider sections that are currently intersecting
  sectionIntersectionRatios.forEach((ratio, sectionIndex) => {
    if (ratio > maxRatio) {
      maxRatio = ratio;
      newActiveSection = sectionIndex;
    }
  });

  // update localStorage if the active section has changed
  if (newActiveSection !== activeSectionIndex) {
    activeSectionIndex = newActiveSection;

    if (
      typeof window !== 'undefined' &&
      newActiveSection !== null &&
      newActiveSection in SectionIndexToStartingAnchor
    ) {
      window.localStorage.setItem(
        SUBFLOW_STARTING_KIT_KEY,
        SectionIndexToStartingAnchor[newActiveSection],
      );
    }
  }
}
